import { RESIDENT_HAS_OWN_PHONE } from "../../../constants/constants";

export type CommunicationGenieFieldKey =
  | "problemWithModesOfExpression"
  | "expressionDescribed"
  | "expressionEquipmentUsed"
  | "problemWithHearing"
  | "hearingDescribed"
  | "hearingEquipmentUsed"
  | "problemWithVision"
  | "visionDescribed"
  | "visionEquipmentUsed"
  | "abilityToUsePhone"
  | "residentPhoneNumber"
  | "howResidentMakesSelfUnderstood"
  | "howResidentUnderstandOthers"
  | "anyStrengthAndAbilities"
  | "anyAssistanceRequired"
  | "anyComments";

export interface CommunicationGenieSectionValues {
  problemWithModesOfExpression?: boolean;
  expressionDescribed?: string;
  expressionEquipmentUsed?: string;
  problemWithHearing?: boolean;
  hearingDescribed?: string;
  hearingEquipmentUsed?: string;
  problemWithVision?: boolean;
  visionDescribed?: string;
  visionEquipmentUsed?: string;
  abilityToUsePhone?: string | boolean;
  residentPhoneNumber?: string;
  howResidentMakesSelfUnderstood?: string;
  howResidentUnderstandOthers?: string;
  anyStrengthAndAbilities?: string;
  anyAssistanceRequired?: string;
  anyComments?: string;
}

type GenieFieldType = "boolean" | "text" | "choice";
type GenieApplyMode = "replace" | "append";

interface CommunicationGenieFieldDefinition {
  key: CommunicationGenieFieldKey;
  label: string;
  type: GenieFieldType;
  applyMode: GenieApplyMode;
}

export interface CommunicationGenieOption {
  id: string;
  label: string;
  description: string;
  groupLabel?: string;
  updates: Partial<CommunicationGenieSectionValues>;
}

export interface CommunicationGenieQuestion {
  id: string;
  prompt: string;
  helperText: string;
  options: CommunicationGenieOption[];
  editableFieldKeys: CommunicationGenieFieldKey[];
}

const COMMUNICATION_GENIE_FIELD_DEFINITIONS: Record<CommunicationGenieFieldKey, CommunicationGenieFieldDefinition> = {
  problemWithModesOfExpression: { key: "problemWithModesOfExpression", label: "Any problems with modes of expression", type: "boolean", applyMode: "replace" },
  expressionDescribed: { key: "expressionDescribed", label: "Expression described", type: "text", applyMode: "append" },
  expressionEquipmentUsed: { key: "expressionEquipmentUsed", label: "Expression equipment used", type: "text", applyMode: "append" },
  problemWithHearing: { key: "problemWithHearing", label: "Any problems with hearing", type: "boolean", applyMode: "replace" },
  hearingDescribed: { key: "hearingDescribed", label: "Hearing described", type: "text", applyMode: "append" },
  hearingEquipmentUsed: { key: "hearingEquipmentUsed", label: "Hearing equipment used", type: "text", applyMode: "append" },
  problemWithVision: { key: "problemWithVision", label: "Any problems with vision", type: "boolean", applyMode: "replace" },
  visionDescribed: { key: "visionDescribed", label: "Vision described", type: "text", applyMode: "append" },
  visionEquipmentUsed: { key: "visionEquipmentUsed", label: "Vision equipment used", type: "text", applyMode: "append" },
  abilityToUsePhone: { key: "abilityToUsePhone", label: "Ability to use phone", type: "choice", applyMode: "replace" },
  residentPhoneNumber: { key: "residentPhoneNumber", label: "Resident phone number", type: "text", applyMode: "replace" },
  howResidentMakesSelfUnderstood: { key: "howResidentMakesSelfUnderstood", label: "How resident makes self understood", type: "text", applyMode: "append" },
  howResidentUnderstandOthers: { key: "howResidentUnderstandOthers", label: "How resident understand others", type: "text", applyMode: "append" },
  anyStrengthAndAbilities: { key: "anyStrengthAndAbilities", label: "Strengths and abilities", type: "text", applyMode: "append" },
  anyAssistanceRequired: { key: "anyAssistanceRequired", label: "Assistance required", type: "text", applyMode: "append" },
  anyComments: { key: "anyComments", label: "Any overall comments", type: "text", applyMode: "append" },
};

export const getCommunicationGenieFieldLabel = (fieldKey: CommunicationGenieFieldKey) =>
  COMMUNICATION_GENIE_FIELD_DEFINITIONS[fieldKey]?.label || fieldKey;

export const COMMUNICATION_GENIE_QUESTIONS: CommunicationGenieQuestion[] = [
  {
    id: "expression",
    prompt: "What best describes the resident's speech, expression, and communication style needs?",
    helperText: "Choose all that apply. Genie will fill expression concerns, how the resident communicates, helpful tools, and the caregiver approach that works best.",
    editableFieldKeys: [
      "expressionDescribed",
      "expressionEquipmentUsed",
      "howResidentMakesSelfUnderstood",
      "anyAssistanceRequired",
    ],
    options: [
      {
        id: "expression_none",
        label: "No major expression concerns",
        description: "Resident can usually express needs clearly.",
        groupLabel: "Communication details",
        updates: {
          problemWithModesOfExpression: false,
          howResidentMakesSelfUnderstood: "Resident is usually able to express needs, preferences, and discomfort clearly during daily care.",
        },
      },
      {
        id: "expression_word_finding",
        label: "Difficulty finding words",
        description: "Needs more time to organize thoughts or respond.",
        groupLabel: "Communication details",
        updates: {
          problemWithModesOfExpression: true,
          expressionDescribed: "Resident may have difficulty finding words and may need extra time to organize thoughts before responding.",
          howResidentMakesSelfUnderstood: "Resident communicates needs verbally but may pause, repeat, or simplify wording before finishing a request.",
          anyAssistanceRequired: "Caregiver should allow extra response time and avoid rushing the resident during conversation.",
        },
      },
      {
        id: "expression_unclear_speech",
        label: "Speech can be unclear",
        description: "Words may be hard to understand at times.",
        groupLabel: "Communication details",
        updates: {
          problemWithModesOfExpression: true,
          expressionDescribed: "Resident's speech may be unclear or difficult to understand at times, especially when tired or frustrated.",
          anyAssistanceRequired: "Caregiver should listen patiently, repeat back what was heard, and confirm meaning when speech is unclear.",
        },
      },
      {
        id: "expression_nonverbal",
        label: "Uses gestures or nonverbal cues",
        description: "Relies on pointing, facial expression, or behavior cues.",
        groupLabel: "Communication details",
        updates: {
          problemWithModesOfExpression: true,
          expressionDescribed: "Resident often relies on gestures, facial expression, pointing, and other nonverbal cues to communicate needs.",
          howResidentMakesSelfUnderstood: "Resident usually makes needs known through gestures, facial expression, behavior cues, and limited verbal statements.",
        },
      },
      {
        id: "expression_delayed_processing",
        label: "Needs extra processing time before responding",
        description: "May pause for a while before answering or organizing thoughts.",
        groupLabel: "Communication details",
        updates: {
          problemWithModesOfExpression: true,
          expressionDescribed: "Resident may need extra processing time before responding and may pause while organizing thoughts or finding words.",
          anyAssistanceRequired: "Caregiver should allow extra processing time, avoid rapid follow-up questions, and pause long enough for the resident to respond.",
        },
      },
      {
        id: "expression_frustration",
        label: "Becomes frustrated when not understood",
        description: "Communication breakdown can increase distress or withdrawal.",
        groupLabel: "Communication details",
        updates: {
          problemWithModesOfExpression: true,
          expressionDescribed: "Resident may become frustrated, withdraw, or repeat self when not understood clearly during conversation.",
          anyComments: "Communication breakdown may increase frustration, so caregiver should slow down and confirm understanding calmly.",
        },
      },
      {
        id: "expression_device",
        label: "Uses communication aid",
        description: "Uses a board, writing, or another communication tool.",
        groupLabel: "Communication details",
        updates: {
          problemWithModesOfExpression: true,
          expressionEquipmentUsed: "Resident uses a communication aid such as writing, picture cues, or a communication board as needed.",
          anyAssistanceRequired: "Caregiver should make sure the resident's communication aid is available and encourage its use during care.",
        },
      },
      {
        id: "expression_intervention_extra_time",
        label: "Give extra response time",
        description: "Pause and allow the resident enough time to answer.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithModesOfExpression: true,
          anyAssistanceRequired: "Caregivers should pause, allow extra response time, and avoid interrupting or rushing the resident during communication.",
        },
      },
      {
        id: "expression_intervention_confirm",
        label: "Repeat back and confirm meaning",
        description: "Check that caregiver understood the resident correctly.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithModesOfExpression: true,
          anyAssistanceRequired: "Caregivers should repeat back what was understood, confirm meaning, and clarify gently when the resident's words are unclear.",
        },
      },
      {
        id: "expression_intervention_offer_choices",
        label: "Offer simple choices or cues",
        description: "Use prompts, yes/no questions, or visual choices.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithModesOfExpression: true,
          anyAssistanceRequired: "Caregivers should use simple prompts, yes/no questions, or short choices to help the resident communicate needs more easily.",
        },
      },
      {
        id: "expression_intervention_one_step",
        label: "Use one-step questions or directions",
        description: "Helpful when too much language at once causes confusion.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithModesOfExpression: true,
          anyAssistanceRequired: "Caregivers should use one-step questions or directions, keep language brief, and avoid overwhelming the resident with too much verbal information at once.",
        },
      },
      {
        id: "expression_intervention_tools_ready",
        label: "Keep communication tools within reach",
        description: "Useful when the resident depends on writing, gestures, or visual supports.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithModesOfExpression: true,
          expressionEquipmentUsed: "Resident may benefit from communication supports such as writing tools, picture cues, or a communication board kept readily available.",
          anyAssistanceRequired: "Caregivers should keep communication aids within reach and prompt their use when verbal expression becomes difficult.",
        },
      },
      {
        id: "expression_intervention_change",
        label: "Report sudden communication change",
        description: "Important when new confusion, silence, or major decline appears.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithModesOfExpression: true,
          anyComments: "Caregiver should report sudden change in communication ability, new confusion, inability to express needs, or a major drop from baseline promptly.",
        },
      },
    ],
  },
  {
    id: "hearing",
    prompt: "What hearing-related support helps the resident understand others best?",
    helperText: "Choose all that apply. Genie will fill hearing concerns, helpful listening conditions, equipment needs, and caregiver communication strategies.",
    editableFieldKeys: [
      "hearingDescribed",
      "hearingEquipmentUsed",
      "howResidentUnderstandOthers",
      "anyAssistanceRequired",
    ],
    options: [
      {
        id: "hearing_none",
        label: "No major hearing concerns",
        description: "Usually hears and understands routine conversation.",
        groupLabel: "Communication details",
        updates: {
          problemWithHearing: false,
          howResidentUnderstandOthers: "Resident is usually able to hear and understand routine conversation and caregiver instructions.",
        },
      },
      {
        id: "hearing_hard_of_hearing",
        label: "Hard of hearing",
        description: "Needs louder or clearer speech.",
        groupLabel: "Communication details",
        updates: {
          problemWithHearing: true,
          hearingDescribed: "Resident is hard of hearing and may miss parts of conversation unless speech is clear and direct.",
          howResidentUnderstandOthers: "Resident understands others best when caregiver speak clearly, face the resident, and repeat important information.",
        },
      },
      {
        id: "hearing_aids",
        label: "Uses hearing aids",
        description: "Needs hearing aids or hearing support equipment.",
        groupLabel: "Communication details",
        updates: {
          problemWithHearing: true,
          hearingEquipmentUsed: "Resident uses hearing aids or other hearing support equipment as needed.",
        },
      },
      {
        id: "hearing_quiet_environment",
        label: "Needs quiet environment",
        description: "Background noise makes communication harder.",
        groupLabel: "Communication details",
        updates: {
          problemWithHearing: true,
          anyAssistanceRequired: "Caregiver should reduce background noise and move to a quieter area when giving important information.",
        },
      },
      {
        id: "hearing_face_to_face",
        label: "Benefits from face-to-face cues",
        description: "Does better when the speaker faces the resident.",
        groupLabel: "Communication details",
        updates: {
          problemWithHearing: true,
          anyAssistanceRequired: "Caregiver should face the resident directly and use calm, clear speech during conversation.",
        },
      },
      {
        id: "hearing_processing",
        label: "Needs slower speech and pauses",
        description: "The resident may hear better when speech is slowed down and spaced out.",
        groupLabel: "Communication details",
        updates: {
          problemWithHearing: true,
          hearingDescribed: "Resident may understand better when caregiver speak slowly, pause between key points, and avoid speaking too quickly.",
          anyAssistanceRequired: "Caregiver should slow the pace of speech, pause between ideas, and check understanding before moving on.",
        },
      },
      {
        id: "hearing_intervention_repeat",
        label: "Repeat key information",
        description: "Important instructions may need to be said more than once.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithHearing: true,
          anyAssistanceRequired: "Caregivers should repeat key information as needed and check understanding before ending the conversation.",
        },
      },
      {
        id: "hearing_intervention_gain_attention",
        label: "Get attention before speaking",
        description: "Make sure the resident is focused first.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithHearing: true,
          anyAssistanceRequired: "Caregivers should get the resident's attention before speaking and avoid talking while turned away or from another room.",
        },
      },
      {
        id: "hearing_intervention_check_equipment",
        label: "Check hearing aids or equipment",
        description: "Confirm devices are in place and working.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithHearing: true,
          anyAssistanceRequired: "Caregivers should help check that hearing aids or other hearing devices are available, in place, and functioning as expected.",
        },
      },
      {
        id: "hearing_intervention_quiet",
        label: "Move important conversations to quieter spaces",
        description: "Helpful when background noise causes the resident to miss details.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithHearing: true,
          anyAssistanceRequired: "Caregivers should move important conversations to quieter spaces when possible and reduce competing noise before giving instructions.",
        },
      },
    ],
  },
  {
    id: "vision",
    prompt: "What vision-related communication support does the resident need?",
    helperText: "Choose all that apply. Genie will fill vision concerns, environmental supports, equipment, and how caregiver should present information.",
    editableFieldKeys: [
      "visionDescribed",
      "visionEquipmentUsed",
      "howResidentUnderstandOthers",
      "anyAssistanceRequired",
    ],
    options: [
      {
        id: "vision_none",
        label: "No major vision concerns",
        description: "Usually sees well enough for routine care tasks.",
        groupLabel: "Communication details",
        updates: {
          problemWithVision: false,
        },
      },
      {
        id: "vision_reduced",
        label: "Reduced vision",
        description: "Needs extra verbal orientation or help locating items.",
        groupLabel: "Communication details",
        updates: {
          problemWithVision: true,
          visionDescribed: "Resident has reduced vision that may affect identifying items, reading print, and following visual cues.",
          anyAssistanceRequired: "Caregiver should explain the environment clearly and place important items within a consistent, easy-to-find location.",
        },
      },
      {
        id: "vision_glasses",
        label: "Uses glasses or visual aids",
        description: "Needs glasses or another visual support device.",
        groupLabel: "Communication details",
        updates: {
          problemWithVision: true,
          visionEquipmentUsed: "Resident uses glasses or other visual aids as needed.",
        },
      },
      {
        id: "vision_orientation",
        label: "Needs verbal orientation",
        description: "Benefits from verbal explanation instead of visual-only cues.",
        groupLabel: "Communication details",
        updates: {
          problemWithVision: true,
          howResidentUnderstandOthers: "Resident understands others better when information is explained verbally instead of relying on visual-only cues.",
        },
      },
      {
        id: "vision_lighting",
        label: "Needs good lighting and contrast",
        description: "Better communication in a well-lit setup.",
        groupLabel: "Communication details",
        updates: {
          problemWithVision: true,
          anyAssistanceRequired: "Caregiver should maintain good lighting and high-contrast setup when reviewing information with the resident.",
        },
      },
      {
        id: "vision_written_material",
        label: "Written information is hard to use independently",
        description: "The resident may miss print-based instructions without verbal backup.",
        groupLabel: "Communication details",
        updates: {
          problemWithVision: true,
          visionDescribed: "Printed information, labels, or written instructions may be hard for the resident to use independently because of reduced vision.",
          howResidentUnderstandOthers: "Resident understands others best when important information is explained verbally rather than relying on printed material alone.",
        },
      },
      {
        id: "vision_intervention_announce",
        label: "Announce approach and explain surroundings",
        description: "Use verbal orientation before touching or moving items.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithVision: true,
          anyAssistanceRequired: "Caregivers should announce themselves, explain surroundings, and describe what they are doing before starting care.",
        },
      },
      {
        id: "vision_intervention_place_items",
        label: "Keep items in consistent locations",
        description: "Helps the resident find needed items more easily.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithVision: true,
          anyAssistanceRequired: "Caregivers should keep commonly used items in consistent locations and verbally guide the resident to them when needed.",
        },
      },
      {
        id: "vision_intervention_reading_support",
        label: "Read or explain visual information aloud",
        description: "Do not rely on print-only instructions.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithVision: true,
          anyAssistanceRequired: "Caregivers should read important printed information aloud and explain visual materials verbally when needed.",
        },
      },
      {
        id: "vision_intervention_contrast",
        label: "Use contrast, placement, and item consistency",
        description: "Helpful when the resident finds items more easily with stable setup.",
        groupLabel: "Caregiver interventions",
        updates: {
          problemWithVision: true,
          anyAssistanceRequired: "Caregivers should keep key items in consistent places, use good contrast when possible, and verbally orient the resident to item location during care.",
        },
      },
    ],
  },
  {
    id: "phone",
    prompt: "What is the resident's phone use and contact support status?",
    helperText: "Choose what best fits. Use these options to capture whether the resident needs setup, reminders, or caregiver help to make or receive calls.",
    editableFieldKeys: ["residentPhoneNumber", "anyAssistanceRequired", "anyComments"],
    options: [
      {
        id: "phone_independent",
        label: "Independent",
        description: "Can use the phone independently.",
        groupLabel: "Phone details",
        updates: {
          abilityToUsePhone: "Independent",
        },
      },
      {
        id: "phone_assistance",
        label: "Assistance needed",
        description: "Needs reminders or setup help.",
        groupLabel: "Phone details",
        updates: {
          abilityToUsePhone: "Assistance Needed",
          anyAssistanceRequired: "Caregiver may need to help set up the phone, dial numbers, or provide reminders to support phone use.",
        },
      },
      {
        id: "phone_dependent",
        label: "Dependent",
        description: "Cannot use the phone without full support.",
        groupLabel: "Phone details",
        updates: {
          abilityToUsePhone: "Dependent",
          anyAssistanceRequired: "Resident is dependent on caregiver assistance for phone use and communication setup.",
        },
      },
      {
        id: "phone_own_phone",
        label: "Has own phone",
        description: "Resident has a personal phone number available.",
        groupLabel: "Phone details",
        updates: {
          abilityToUsePhone: RESIDENT_HAS_OWN_PHONE,
          residentPhoneNumber: "(   )    -    ",
        },
      },
      {
        id: "phone_no_personal",
        label: "No personal phone",
        description: "Uses facility or caregiver support instead.",
        groupLabel: "Phone details",
        updates: {
          anyComments: "Resident does not have a personal phone and may rely on facility or caregiver support for phone communication.",
        },
      },
      {
        id: "phone_intervention_dialing",
        label: "Help with dialing or setup",
        description: "Caregivers assist with basic phone setup tasks.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyAssistanceRequired: "Caregivers should help with phone setup, dialing, speaker use, or call connection when the resident is unable to manage those steps alone.",
        },
      },
      {
        id: "phone_intervention_reminders",
        label: "Provide reminders for calls",
        description: "Resident may need cueing to make or answer calls.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyAssistanceRequired: "Caregivers should provide reminders, cueing, or scheduling support for important calls when needed.",
        },
      },
      {
        id: "phone_intervention_privacy",
        label: "Support privacy and calm setup during calls",
        description: "Helpful when the resident does better with a quiet, low-stress calling routine.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyAssistanceRequired: "Caregivers should help create a calm, private phone setup, reduce distractions, and stay available for support without taking over unnecessarily.",
        },
      },
    ],
  },
  {
    id: "support",
    prompt: "What overall communication strengths, risks, and caregiver strategies should be documented?",
    helperText: "Choose all that apply. Genie will fill communication strengths, caregiver support strategies, and overall comments for handoff consistency.",
    editableFieldKeys: [
      "anyStrengthAndAbilities",
      "anyAssistanceRequired",
      "anyComments",
    ],
    options: [
      {
        id: "support_strength_verbal",
        label: "Can express basic needs",
        description: "Has a clear strength in communicating familiar needs.",
        updates: {
          anyStrengthAndAbilities: "Resident can usually express basic needs, preferences, and discomfort when given time and familiar support.",
        },
      },
      {
        id: "support_strength_relationship",
        label: "Responds well to familiar caregiver",
        description: "Communicates better with familiar caregivers.",
        updates: {
          anyStrengthAndAbilities: "Resident often communicates best with familiar caregiver who understand personal cues and communication patterns.",
        },
      },
      {
        id: "support_simple_cues",
        label: "Needs short simple directions",
        description: "Benefits from one-step communication.",
        updates: {
          anyAssistanceRequired: "Caregiver should use short phrases, one-step directions, and calm simple cueing during communication.",
        },
      },
      {
        id: "support_repeat_confirm",
        label: "Needs repetition and confirmation",
        description: "Benefits from repeat-back and confirmation.",
        updates: {
          anyAssistanceRequired: "Caregiver should repeat information as needed and confirm understanding before moving on.",
          anyComments: "Use simple repeat-back or confirmation questions to support understanding.",
        },
      },
      {
        id: "support_extra_time",
        label: "Needs extra response time",
        description: "Responds better when not rushed.",
        updates: {
          anyAssistanceRequired: "Allow extra processing and response time so the resident can participate successfully in communication.",
        },
      },
      {
        id: "support_quiet_setting",
        label: "Communicates better one-on-one",
        description: "Low-distraction settings help communication.",
        updates: {
          anyComments: "Communication is often most successful during one-on-one interaction in a calm, low-distraction setting.",
        },
      },
      {
        id: "support_escalate_change",
        label: "Sudden communication change should be escalated",
        description: "Use when abrupt decline would be clinically important.",
        updates: {
          anyComments: "A sudden change in communication, comprehension, responsiveness, or ability to report needs should be escalated promptly for further assessment.",
        },
      },
      {
        id: "support_nonverbal_observation",
        label: "Watch nonverbal signs of unmet needs",
        description: "Helpful when the resident cannot always explain discomfort clearly.",
        updates: {
          anyStrengthAndAbilities: "Resident may still communicate needs through facial expression, body language, tone, behavior, and other familiar nonverbal cues.",
          anyAssistanceRequired: "Caregiver should watch for nonverbal signs of pain, fear, frustration, or unmet needs when verbal communication is limited or unclear.",
        },
      },
      {
        id: "support_consistency",
        label: "Use the same communication approach across shifts",
        description: "Consistency helps reduce confusion and caregiver-to-caregiver variation.",
        updates: {
          anyAssistanceRequired: "Caregiver should use a consistent communication approach across shifts, including pacing, cueing style, confirmation method, and any needed supports or devices.",
        },
      },
    ],
  },
];

export interface CommunicationGeniePreview {
  valueUpdates: Partial<CommunicationGenieSectionValues>;
  reviewItems: Array<{
    fieldKey: CommunicationGenieFieldKey;
    label: string;
    type: GenieFieldType;
    value: string | boolean;
  }>;
}

export const appendCommunicationGenieSuggestion = (existingValue?: string, nextSuggestion?: string) => {
  const existingText = String(existingValue || "").trim();
  const suggestionText = String(nextSuggestion || "").trim();
  if (!suggestionText) {
    return existingText;
  }
  if (!existingText) {
    return suggestionText;
  }
  if (existingText.includes(suggestionText)) {
    return existingText;
  }
  return `${existingText}\n\n${suggestionText}`;
};

export const buildCommunicationGenieStepDrafts = (
  question: CommunicationGenieQuestion,
  selectedOptionIds: string[] = [],
): Partial<Record<CommunicationGenieFieldKey, string>> => {
  const drafts: Partial<Record<CommunicationGenieFieldKey, string[]>> = {};
  question.options
    .filter((option) => selectedOptionIds.includes(option.id))
    .forEach((option) => {
      question.editableFieldKeys.forEach((fieldKey) => {
        const nextValue = String(option.updates?.[fieldKey] || "").trim();
        if (!nextValue) {
          return;
        }
        const currentDrafts = drafts[fieldKey] || [];
        if (!currentDrafts.includes(nextValue)) {
          drafts[fieldKey] = [...currentDrafts, nextValue];
        }
      });
    });

  return Object.fromEntries(
    Object.entries(drafts).map(([fieldKey, fieldValues]) => [fieldKey, (fieldValues || []).join(" ")]),
  ) as Partial<Record<CommunicationGenieFieldKey, string>>;
};

export const buildCommunicationGenieAppendPreview = (
  currentValues: CommunicationGenieSectionValues,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Partial<Record<CommunicationGenieFieldKey, string>>> = {},
) => {
  const valueUpdates: Partial<CommunicationGenieSectionValues> = {};
  const draftOverrideFields = new Set<CommunicationGenieFieldKey>();

  COMMUNICATION_GENIE_QUESTIONS.forEach((question) => {
    const selectedOptionIds = Array.isArray(selectedOptions[question.id]) ? selectedOptions[question.id] : [];
    const selectedQuestionOptions = question.options.filter((option) => selectedOptionIds.includes(option.id));

    selectedQuestionOptions.forEach((selectedOption) => {
      Object.entries(selectedOption.updates).forEach(([fieldKey, fieldValue]) => {
        const typedFieldKey = fieldKey as CommunicationGenieFieldKey;
        const fieldDefinition = COMMUNICATION_GENIE_FIELD_DEFINITIONS[typedFieldKey];
        if (!fieldDefinition) {
          return;
        }

        if (fieldDefinition.type === "boolean") {
          if (fieldValue === true || valueUpdates[typedFieldKey] === undefined) {
            valueUpdates[typedFieldKey] = fieldValue as never;
          }
          return;
        }

        if (fieldDefinition.type === "choice") {
          valueUpdates[typedFieldKey] = fieldValue as never;
          return;
        }

        const nextText = String(fieldValue || "").trim();
        if (!nextText) {
          return;
        }
        const previousText = String(valueUpdates[typedFieldKey] || "").trim();
        valueUpdates[typedFieldKey] = (
          previousText
            ? `${previousText} ${nextText}`.trim()
            : nextText
        ) as never;
      });
    });

    const questionDraftFields = questionDrafts[question.id] || {};
    Object.entries(questionDraftFields).forEach(([fieldKey, fieldValue]) => {
      const typedFieldKey = fieldKey as CommunicationGenieFieldKey;
      const fieldDefinition = COMMUNICATION_GENIE_FIELD_DEFINITIONS[typedFieldKey];
      const nextValue = String(fieldValue || "").trim();
      if (!fieldDefinition || !nextValue) {
        return;
      }
      draftOverrideFields.add(typedFieldKey);
      valueUpdates[typedFieldKey] = nextValue as never;
    });
  });

  const finalizedUpdates: Partial<CommunicationGenieSectionValues> = {};
  Object.entries(valueUpdates).forEach(([fieldKey, fieldValue]) => {
    const typedFieldKey = fieldKey as CommunicationGenieFieldKey;
    const fieldDefinition = COMMUNICATION_GENIE_FIELD_DEFINITIONS[typedFieldKey];
    if (!fieldDefinition) {
      return;
    }

    if (fieldDefinition.type === "text") {
      const nextText = String(fieldValue || "").trim();
      if (!nextText) {
        return;
      }
      finalizedUpdates[typedFieldKey] = (
        draftOverrideFields.has(typedFieldKey)
          ? nextText
          : fieldDefinition.applyMode === "append"
          ? appendCommunicationGenieSuggestion(String(currentValues?.[typedFieldKey] || ""), nextText)
          : nextText
      ) as never;
      return;
    }

    finalizedUpdates[typedFieldKey] = fieldValue as never;
  });

  const reviewItems = Object.entries(finalizedUpdates).map(([fieldKey, fieldValue]) => {
    const definition = COMMUNICATION_GENIE_FIELD_DEFINITIONS[fieldKey as CommunicationGenieFieldKey];
    return {
      fieldKey: fieldKey as CommunicationGenieFieldKey,
      label: definition.label,
      type: definition.type,
      value: fieldValue as string | boolean,
    };
  });

  return {
    valueUpdates: finalizedUpdates,
    reviewItems,
  } as CommunicationGeniePreview;
};
